// Expense-domain XLSX helpers for the bulk upload flow.
//
// Three responsibilities:
//   buildBulkExpensesXlsx — generate the fillable template with dropdowns
//                           sourced from the school's live category /
//                           sub-category catalog.
//   parseBulkExpensesXlsx — read an uploaded XLSX, resolve category /
//                           sub-category names to IDs, flag per-row errors.
//   validateBulkExpenseFields — single source of truth for row validation.
//                               Used both at parse time and from the review
//                               UI's inline edit path so a cell edit
//                               re-runs the exact same checks the parser
//                               did.
//
// Everything that touches the network stays out of this file — callers
// supply the already-loaded lookups so templates and parses are deterministic
// and cheap.

import {
  readFirstSheetAsRows,
  writeSheet,
  writeSheetWithReferences,
  type ReferenceList,
  type XlsxColumnSpec,
} from './xlsx';
import type { ExpenseCategory, ExpenseSubCategory } from '../types';

/**
 * Excel caps an inline list-validation formula at 255 characters including
 * the wrapping double quotes. We leave a 5-char safety buffer.
 *
 * Why this matters here: inline list validation (`formulae: ["\"a,b,c\""]`)
 * survives XLSX → Google Sheets conversion perfectly. A hidden-sheet
 * reference (`formulae: ["='_System'!$A$3:$A$20"]`) often does NOT — Google
 * Sheets can drop the hidden sheet or lose the cross-sheet reference during
 * import, leaving the dropdown gone with no visible error.
 *
 * So we prefer inline whenever the list fits, and only reach for the hidden
 * reference sheet when the list is too long to inline.
 */
function inlineListFits(values: string[]): boolean {
  const joined = values.join(',');
  return joined.length <= 250;
}

/** Public (merchant-facing) column names. Order here IS the write order. */
export const BULK_EXPENSES_COLUMNS = [
  'title',
  'amount',
  'date',
  'category',
  'subCategory',
  'notes',
] as const;

export type BulkExpenseEditableFields = {
  title: string;
  amount: string; // kept as string in the review UI so partial edits don't fight the parser
  date: string;   // ISO yyyy-mm-dd or any Date-parseable string
  categoryName?: string;
  subCategoryName?: string;
  notes?: string;
};

export type ParsedBulkExpenseRow = {
  rowNumber: number;
  fields: BulkExpenseEditableFields;
  // Resolved after validation. undefined if the row has an error.
  categoryId?: string;
  subCategoryId?: string;
  parsedAmount?: number;
  parsedDateIso?: string;
  clientStatus: 'ok' | 'error';
  clientError?: string;
  /** Which field the error is about, so the review table marks that cell. */
  errorField?: BulkExpenseErrorField;
};

export type BulkExpenseErrorField = 'title' | 'amount' | 'date' | 'category' | 'subCategory' | 'notes';

export type BulkExpenseLookups = {
  /** Lower-cased name → category document. */
  categoriesByName: Map<string, ExpenseCategory>;
  /** Lower-cased name → list of sub-category docs (names can collide across
   *  categories — the validator picks the one whose categoryId matches). */
  subCategoriesByName: Map<string, ExpenseSubCategory[]>;
  /** The school's today, 'YYYY-MM-DD' — expenses can't be dated after it. */
  today: string;
};

/** Build the lookups a template builder / parser / validator needs from
 *  the raw category + sub-category arrays. Keeps the Map wiring in one
 *  place so callers can't drift. */
export function buildLookups(
  categories: ExpenseCategory[],
  subCategories: ExpenseSubCategory[],
  today: string,
): BulkExpenseLookups {
  const categoriesByName = new Map<string, ExpenseCategory>();
  for (const c of categories) {
    categoriesByName.set(c.name.trim().toLowerCase(), c);
  }
  const subCategoriesByName = new Map<string, ExpenseSubCategory[]>();
  for (const sc of subCategories) {
    const key = sc.name.trim().toLowerCase();
    const bucket = subCategoriesByName.get(key);
    if (bucket) bucket.push(sc);
    else subCategoriesByName.set(key, [sc]);
  }
  return { categoriesByName, subCategoriesByName, today };
}

/**
 * Build the template XLSX. For each dropdown column we prefer inline list
 * validation (so Google Sheets upload keeps the dropdowns), falling back
 * to a hidden reference sheet only when the list is too long to fit in
 * Excel's 255-char inline formula cap.
 */
export async function buildBulkExpensesXlsx(input: {
  categories: ExpenseCategory[];
  subCategories: ExpenseSubCategory[];
}): Promise<Blob> {
  const categoryNames = Array.from(
    new Set(input.categories.map((c) => c.name.trim())),
  )
    .filter(Boolean)
    .sort();

  const subCategoryNames = Array.from(
    new Set(input.subCategories.map((s) => s.name.trim())),
  )
    .filter(Boolean)
    .sort();

  // Example row shows the expected shape. Users are told to delete it in
  // the dialog instructions.
  const exampleRow: (string | number)[] = [
    'Example expense — delete this row',
    0,
    new Date().toISOString().slice(0, 10),
    categoryNames[0] ?? '',
    '',
    '',
  ];

  const categoryInline = categoryNames.length > 0 && inlineListFits(categoryNames);
  const subCategoryInline = subCategoryNames.length > 0 && inlineListFits(subCategoryNames);
  const needsReferenceSheet =
    (categoryNames.length > 0 && !categoryInline) ||
    (subCategoryNames.length > 0 && !subCategoryInline);

  const columns: XlsxColumnSpec[] = [
    { header: 'title', width: 36 },
    {
      header: 'amount',
      width: 14,
      validation: { kind: 'decimal-min', min: 0.01 },
    },
    { header: 'date (yyyy-mm-dd)', width: 18 },
    {
      header: 'category',
      width: 24,
      validation:
        categoryNames.length === 0
          ? undefined
          : categoryInline
            ? { kind: 'list', values: categoryNames }
            : { kind: 'list', values: ['@ref:ValidCategories'] },
    },
    {
      header: 'subCategory',
      width: 24,
      validation:
        subCategoryNames.length === 0
          ? undefined
          : subCategoryInline
            ? { kind: 'list', values: subCategoryNames }
            : { kind: 'list', values: ['@ref:ValidSubCategories'] },
    },
    { header: 'notes', width: 40 },
  ];

  if (!needsReferenceSheet) {
    // Fast path: everything fits inline, single-sheet workbook. This is
    // the Google-Sheets-friendly branch and covers the vast majority of
    // schools (10-30 categories/subcategories each).
    return writeSheet({
      sheetName: 'Expenses',
      columns,
      rows: [exampleRow],
      emptyRowsForValidation: 500,
    });
  }

  // Slow path: at least one list is too long to inline. Build the
  // reference sheet with ONLY the overflowing lists — any inline-fit
  // columns stay inline and don't need to be referenced.
  const references: ReferenceList[] = [];
  if (categoryNames.length > 0 && !categoryInline) {
    references.push({ name: 'ValidCategories', values: categoryNames });
  }
  if (subCategoryNames.length > 0 && !subCategoryInline) {
    references.push({ name: 'ValidSubCategories', values: subCategoryNames });
  }

  return writeSheetWithReferences({
    sheetName: 'Expenses',
    columns,
    rows: [exampleRow],
    emptyRowsForValidation: 500,
    references,
  });
}

/**
 * Validate the editable fields of a row against the school's catalog.
 * Shared between the initial XLSX parse and the review UI's inline edit
 * save flow so both paths raise identical statuses.
 */
export function validateBulkExpenseFields(
  fields: BulkExpenseEditableFields,
  lookups: BulkExpenseLookups,
): Omit<ParsedBulkExpenseRow, 'rowNumber' | 'fields'> {
  const title = fields.title.trim();
  if (!title) {
    return { clientStatus: 'error', clientError: 'title is required', errorField: 'title' };
  }
  if (title.length > 200) {
    return { clientStatus: 'error', clientError: 'title must be 200 characters or fewer', errorField: 'title' };
  }

  const rawAmount = fields.amount?.toString().trim() ?? '';
  if (!rawAmount) {
    return { clientStatus: 'error', clientError: 'amount is required', errorField: 'amount' };
  }
  const amount = Number(rawAmount);
  if (!Number.isFinite(amount) || amount <= 0) {
    return { clientStatus: 'error', clientError: 'amount must be more than zero', errorField: 'amount' };
  }

  const rawDate = fields.date?.toString().trim() ?? '';
  if (!rawDate) {
    return { clientStatus: 'error', clientError: 'date is required', errorField: 'date' };
  }
  // Accept ISO, yyyy-mm-dd, or any Date-parseable string. We DO NOT silently
  // coerce "weird" input to today — ambiguous dates get rejected so the user
  // can retype them. Explicit > magical.
  const date = new Date(rawDate);
  if (isNaN(date.getTime())) {
    return { clientStatus: 'error', clientError: `date "${rawDate}" is not a valid date`, errorField: 'date' };
  }
  const parsedDateIso = date.toISOString();
  // A date-only cell is that day; anything with a time is read as the local day.
  const day = /^\d{4}-\d{2}-\d{2}$/.test(rawDate) ? rawDate : parsedDateIso.slice(0, 10);
  if (day > lookups.today) {
    return { clientStatus: 'error', clientError: `date "${rawDate}" is in the future`, errorField: 'date' };
  }

  const rawCategory = fields.categoryName?.trim();
  if (!rawCategory) {
    return { clientStatus: 'error', clientError: 'category is required', errorField: 'category' };
  }
  const categoryMatch = lookups.categoriesByName.get(rawCategory.toLowerCase());
  if (!categoryMatch) {
    return { clientStatus: 'error', clientError: `category "${rawCategory}" is not in the catalog`, errorField: 'category' };
  }
  const categoryId: string = categoryMatch._id;

  const rawSubCategory = fields.subCategoryName?.trim();
  let subCategoryId: string | undefined;
  if (rawSubCategory) {
    const candidates = lookups.subCategoriesByName.get(rawSubCategory.toLowerCase()) ?? [];
    const match = candidates.find((sc) => {
      // sc.categoryId is a string ObjectId OR a populated object. Normalise.
      const parent =
        typeof sc.categoryId === 'string'
          ? sc.categoryId
          : (sc.categoryId as { _id: string })._id;
      return parent === categoryId;
    });
    if (!match) {
      return {
        clientStatus: 'error',
        clientError: `subCategory "${rawSubCategory}" is not in "${rawCategory}"`, errorField: 'subCategory',
      };
    }
    subCategoryId = match._id;
  }

  const notes = fields.notes?.trim() ?? '';
  if (notes.length > 1000) {
    return { clientStatus: 'error', clientError: 'notes must be 1000 characters or fewer', errorField: 'notes' };
  }

  return {
    categoryId,
    subCategoryId,
    parsedAmount: amount,
    parsedDateIso,
    clientStatus: 'ok',
  };
}

export type ParsedBulkExpenses = {
  rows: ParsedBulkExpenseRow[];
  fileError?: string;
};

export async function parseBulkExpensesXlsx(
  file: File,
  lookups: BulkExpenseLookups,
): Promise<ParsedBulkExpenses> {
  const raw = await readFirstSheetAsRows(file);
  if (raw.length < 2) {
    return {
      rows: [],
      fileError: 'The file must have a header row and at least one data row.',
    };
  }

  const headers = raw[0].map((h) => h.trim());
  // Match either the exact header or the header followed by a space-opened
  // suffix (e.g. "date (yyyy-mm-dd)" still matches a lookup for "date").
  // Lets us tack format hints onto user-facing headers without breaking
  // the parser.
  const idx = (name: string) => {
    const lower = name.toLowerCase();
    return headers.findIndex((h) => {
      const hl = h.toLowerCase();
      return hl === lower || hl.startsWith(`${lower} `);
    });
  };
  const titleIdx = idx('title');
  const amountIdx = idx('amount');
  const dateIdx = idx('date');
  const categoryIdx = idx('category');
  const subCategoryIdx = idx('subCategory');
  const notesIdx = idx('notes');

  if (titleIdx === -1 || amountIdx === -1 || dateIdx === -1) {
    return {
      rows: [],
      fileError: `Missing required columns. Need at least title, amount, date; found: ${headers.join(', ')}`,
    };
  }

  const rows: ParsedBulkExpenseRow[] = [];
  for (let i = 1; i < raw.length; i++) {
    const cols = raw[i];
    const title = (cols[titleIdx] ?? '').trim();
    const amount = (cols[amountIdx] ?? '').trim();
    const date = (cols[dateIdx] ?? '').trim();
    const categoryName = categoryIdx === -1 ? '' : (cols[categoryIdx] ?? '').trim();
    const subCategoryName = subCategoryIdx === -1 ? '' : (cols[subCategoryIdx] ?? '').trim();
    const notes = notesIdx === -1 ? '' : (cols[notesIdx] ?? '').trim();

    // Skip fully blank rows so a user who leaves trailing empty rows
    // doesn't see "title is required" ×100.
    if (!title && !amount && !date && !categoryName && !subCategoryName && !notes) {
      continue;
    }

    const fields: BulkExpenseEditableFields = {
      title,
      amount,
      date,
      categoryName: categoryName || undefined,
      subCategoryName: subCategoryName || undefined,
      notes: notes || undefined,
    };
    const validated = validateBulkExpenseFields(fields, lookups);
    rows.push({
      rowNumber: rows.length + 1,
      fields,
      ...validated,
    });
  }

  return { rows };
}

/** Map parsed+validated rows into the wire payload for POST /expenses/bulk-rows.
 *  Only rows with clientStatus === 'ok' are included. */
export function toBulkRowsPayload(
  rows: ParsedBulkExpenseRow[],
): Array<{
  rowNumber: number;
  title: string;
  amount: number;
  date: string;
  categoryId?: string;
  subCategoryId?: string;
  notes?: string;
}> {
  return rows
    .filter((r) => r.clientStatus === 'ok' && r.parsedAmount != null && r.parsedDateIso)
    .map((r) => ({
      rowNumber: r.rowNumber,
      title: r.fields.title.trim(),
      amount: r.parsedAmount as number,
      date: r.parsedDateIso as string,
      categoryId: r.categoryId,
      subCategoryId: r.subCategoryId,
      notes: r.fields.notes?.trim() || undefined,
    }));
}
